import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Quote } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { BLOG_POSTS, getPostBySlug, localized } from "../data/blog";
import SEOHead from "../components/SEOHead";
import { buildBlogPostSchema, BUSINESS_PROFILE } from "../data/seo";

function dateFmt(iso, lang) {
  try {
    const d = new Date(iso);
    const locale = lang === "ar" ? "ar-EG" : lang === "en" ? "en-GB" : "de-DE";
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

function ContentBlock({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mt-12 mb-5 leading-tight">{block.text}</h2>;
    case "h3":
      return <h3 className="text-xl font-bold tracking-tight mt-8 mb-3">{block.text}</h3>;
    case "ul":
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((it, i) => (
            <li key={`li-${i}-${(it || "").slice(0, 20)}`} className="flex items-start gap-3 text-base text-neutral-700 leading-relaxed">
              <span className="w-1.5 h-1.5 bg-[#FF5A00] mt-2.5 rounded-full shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="my-10 relative border-s-4 border-[#FF5A00] ps-6 py-1">
          <Quote size={28} className="text-[#FF5A00] mb-3 opacity-60" />
          <p className="text-xl sm:text-2xl font-bold leading-snug tracking-tight text-neutral-900">{block.text}</p>
        </blockquote>
      );
    case "p":
    default:
      return <p className="my-5 text-base sm:text-lg text-neutral-700 leading-relaxed">{block.text}</p>;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const post = getPostBySlug(slug);

  const title = post ? localized(post, lang, "title") : null;
  const excerpt = post ? localized(post, lang, "excerpt") : null;
  const content = post ? localized(post, lang, "content") : null;

  useEffect(() => {
    if (title) document.title = `${title} — Talkani`;
    window.scrollTo(0, 0);
  }, [title]);

  if (!post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center px-6">
          <h1 className="text-4xl font-black tracking-tighter mb-4">{t.blog.not_found}</h1>
          <Link to="/blog" className="btn-primary"><ArrowLeft size={16} className="rtl:rotate-180" /> {t.blog.back_to_blog}</Link>
        </div>
      </div>
    );
  }

  const related = BLOG_POSTS.filter(p => p.slug !== post.slug).slice(0, 2);

  return (
    <article className="bg-white">
      <SEOHead
        title={title}
        description={excerpt}
        image={`${BUSINESS_PROFILE.url}${post.cover}`}
        url={`${BUSINESS_PROFILE.url}/blog/${post.slug}`}
        type="article"
        lang={lang}
        jsonLd={buildBlogPostSchema(post, lang, { title, excerpt })}
      />
      {/* Hero */}
      <header className="relative bg-[#0A0B0D] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={post.cover} alt="" className="w-full h-full object-cover opacity-30 hero-kenburns" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/80 to-[#0A0B0D]/40" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-[#FF5A00] mb-6 hover:gap-3 transition-all" data-testid="blog-back-btn">
            <ArrowLeft size={16} className="rtl:rotate-180" /> {t.blog.back_to_blog}
          </Link>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-4">{t.blog.eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-6">{title}</h1>
          <p className="text-lg text-neutral-300 leading-relaxed max-w-3xl mb-6">{excerpt}</p>
          <div className="flex items-center gap-6 text-xs text-neutral-400 font-semibold">
            <span className="inline-flex items-center gap-1.5"><Calendar size={13} />{dateFmt(post.date, lang)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={13} />{post.readTime} {t.blog.min_read}</span>
          </div>
        </div>
      </header>

      {/* Body */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {content?.map((block, i) => <ContentBlock key={i} block={block} />)}

          {/* CTA */}
          <div className="mt-16 p-8 bg-[#F8F9FA] border border-neutral-200 rounded-sm" data-testid="blog-cta">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-2">{t.blog.cta_eyebrow}</p>
            <h3 className="text-2xl sm:text-3xl font-black tracking-tighter mb-4">{t.blog.cta_title}</h3>
            <p className="text-neutral-600 mb-6">{t.blog.cta_subtitle}</p>
            <Link to="/book" className="btn-primary" data-testid="blog-cta-book">
              {t.nav.book}
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 lg:py-24 bg-[#F8F9FA] border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-10">{t.blog.related}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map(rp => (
                <Link
                  key={rp.slug}
                  to={`/blog/${rp.slug}`}
                  className="card-lift bg-white border border-neutral-200 overflow-hidden rounded-sm flex flex-col"
                  data-testid={`blog-related-${rp.slug}`}
                >
                  <div className="aspect-[16/9] bg-neutral-200 overflow-hidden">
                    <img src={rp.cover} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold tracking-tight mb-2 leading-snug">{localized(rp, lang, "title")}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{localized(rp, lang, "excerpt")}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
